import { Animator as AnimatorInterface, AnimatorClass } from "./Animators/Animator"
import { Interpolation, InterpolationOptions, ValueInterpolation } from "../interpolation"
import { Animatable, AnimatableObject, isAnimatable } from "./Animatable"
import { DriverClass, AnimationDriver } from "./Drivers/AnimationDriver"
import { MainLoopAnimationDriver } from "./Drivers/MainLoopDriver"
import { PrecalculatedAnimator } from "./Animators/PrecalculatedAnimator"
import { BezierAnimator } from "./Animators/BezierAnimator"
import { ColorMixModelType } from "../render/types/Color/types"

type TargetObject<T> = { [K in keyof T]: T[K] | Animatable<T[K]> }

export type FramerAnimationState = "idle" | "running" | "finished"

/**
 * @deprecated Use {@link FrameProps.transition} instead
 */
export interface AnimationOptions<Value> extends InterpolationOptions {
    /**
     * @internal
     */
    customInterpolation?: Interpolation<Value>
    /**
     * @internal
     */
    precalculate: boolean
}

const DefaultAnimationOptions: AnimationOptions<any> = {
    precalculate: false,
    colorModel: ColorMixModelType.HUSL,
}

/**
 * The animation object returned by the {@link (animate:function)} functions
 * @remarks
 * Can be used to control a animation or wait for it to finish. You never create a FramerAnimation yourself, but store the return type from the animate function.
 * ```jsx
 * const animation = animate.ease(value, 100)
 * await animation.finished()
 * const animation = animate.spring(value, 200)
 * animation.cancel()
 * ```
 * @privateRemarks
 * This could be called just Animation, but it's type would clash with
 * javascript's native Animation: https://developer.mozilla.org/en-US/docs/Web/API/Animation
 * So if you forget the import, you would get weird errors.
 *
 * Also, this class follows the native Animation as much as possible.
 * @public
 * @deprecated Use the {@link useAnimation} hook instead
 */
export class FramerAnimation<Value, AnimatorOptions> {
    /**
     * @internal
     */
    private driver: AnimationDriver<AnimatorInterface<Value, AnimatorOptions>, Value, AnimatorOptions>
    /**
     * @internal
     */
    constructor(
        target: Animatable<Value> | AnimatableObject<Value>,
        from: Value,
        to: Value,
        animatorClass?: AnimatorClass<Value, AnimatorOptions>,
        options?: Partial<AnimatorOptions & AnimationOptions<Value>>,
        driverClass: DriverClass<
            AnimatorInterface<Value, AnimatorOptions>,
            Value,
            AnimatorOptions
        > = MainLoopAnimationDriver
    ) {
        this.resetFinishedPromise()
        const animationOptions: AnimationOptions<Value> = { ...DefaultAnimationOptions }
        const animatorOptions: Partial<AnimatorOptions> = {}
        if (options) {
            Object.assign(animationOptions, options)
            Object.assign(animatorOptions, options)
        }
        let interpolation: Interpolation<Value>
        if (animationOptions.customInterpolation) {
            interpolation = animationOptions.customInterpolation
        } else {
            interpolation = new ValueInterpolation(options)
        }
        let animator: AnimatorInterface<Value, AnimatorOptions>
        if (!animatorClass) {
            animator = new BezierAnimator({}, interpolation)
        } else {
            animator = new animatorClass(animatorOptions, interpolation)
        }
        if (animationOptions.precalculate) {
            animator = new PrecalculatedAnimator({ animator })
        }
        animator.setFrom(from)
        animator.setTo(to)
        const updateCallback = (value: Value) => {
            FramerAnimation.driverCallbackHandler(target, value)
        }

        const finishedCallback = (isFinished: boolean) => {
            if (isFinished) {
                FramerAnimation.driverCallbackHandler(target, to)
                if (this.playStateSource === "running") {
                    this.playStateValue = "finished"
                }
            }
        }
        this.driver = new driverClass(animator, updateCallback, finishedCallback)
    }

    /**
     * @internal
     */
    private static driverCallbackHandler<Value>(target: Animatable<Value> | AnimatableObject<Value>, value: Value) {
        if (isAnimatable(target)) {
            target.set(value)
        } else {
            const targetObject = target as TargetObject<Value>
            Animatable.transaction(update => {
                for (const key in targetObject) {
                    const targetValue = targetObject[key]
                    if (isAnimatable(targetValue)) {
                        update(targetValue, value[key])
                    } else {
                        targetObject[key] = value[key]
                    }
                }
            })
        }
    }

    /**
     * @internal
     */
    private playStateSource: FramerAnimationState = "idle"

    /**
     * @internal
     */
    private get playStateValue(): FramerAnimationState {
        return this.playStateSource
    }

    /**
     * @internal
     */
    private set playStateValue(value: FramerAnimationState) {
        if (value !== this.playStateSource) {
            const oldValue = value
            this.playStateSource = value
            switch (value) {
                case "idle":
                    if (oldValue === "running") {
                        this.oncancel && this.oncancel()
                    }
                    this.readyResolve && this.readyResolve()
                    this.resetReadyPromise()
                    break
                case "finished":
                    if (oldValue === "idle") {
                        // tslint:disable-next-line:no-console
                        console.warn("Bad state transition")
                        break
                    }
                    this.onfinish && this.onfinish()
                    this.finishedResolve && this.finishedResolve()
                    break
                case "running":
                    this.resetReadyPromise()
                    break
            }

            if (oldValue === "finished") {
                this.resetFinishedPromise()
            }

            if (value === "finished") {
                // Jump to idle state:
                this.playStateValue = "idle"
            }
        }
    }

    /**
     * @internal
     */
    get playState(): FramerAnimationState {
        return this.playStateValue
    }

    /**
     * @internal
     */
    onfinish: undefined | (() => void)
    /**
     * @internal
     */
    oncancel: undefined | (() => void)

    /**
     * @internal
     */
    private readyPromise: Promise<void> = Promise.resolve()

    /**
     * @internal
     */
    private readyResolve: null | (() => void)

    /**
     * @internal
     */
    private resetReadyPromise() {
        this.readyResolve = null
        this.readyPromise = new Promise((resolve, reject) => {
            this.readyResolve = resolve
        })
    }

    /**
     * Wait for the animation to be ready to play.
     * @remarks
     * ```jsx
     * const animation = animate.ease(value, 100)
     * animation.ready().then(() => {
     *    // Animation is ready
     * })

     * // async/await syntax
     * const animation = animate.ease(value, 100)
     * await animation.ready()
     * // Animation is ready
     * ```
     * @returns Promise that is resolved when the animation is ready to play
     * @public
     */
    get ready(): Promise<void> {
        return this.readyPromise
    }

    /**
     * @internal
     */
    private finishedPromise: Promise<void>
    /**
     * @internal
     */
    private finishedResolve: null | (() => void)
    /**
     * @internal
     */
    private finishedReject: null | ((reason: any) => void)
    /**
     * @internal
     */
    private resetFinishedPromise() {
        this.finishedResolve = null
        this.finishedReject = null
        this.finishedPromise = new Promise((resolve, reject) => {
            this.finishedResolve = resolve
            this.finishedReject = reject
        })
        this.finishedPromise.catch(reason => {
            // Eat the exception that will occur when no 'reject' handler
            // is set.
        })
    }

    /**
     * Wait for the animation to be finished.
     * @remarks
     * ```jsx
     * // async/await syntax
     * const animation = animate.ease(value, 100)
     * await animation.finished()
     * // Animation is finished
     *
     *
     * const animation = animate.ease(value, 100)
     * animation.ready().then(() => {
     *    // Animation is finished
     * })
     * ```
     * @returns Promise that is resolved when the animation is ready to play
     * @public
     */
    get finished(): Promise<void> {
        return this.finishedPromise
    }

    /**
     * @internal
     */
    play() {
        this.playStateValue = "running"
        this.driver.play()
    }

    /**
     * Cancels the animation if it is still running.
     * @remarks
     * ```jsx
     * const animation = animate.ease(value, 100, {duration: 3})
     * setTimeout(() => animation.cancel(), 500)
     * ```
     * @public
     */
    cancel() {
        if (this.playStateValue !== "running") {
            return
        }

        this.driver.cancel()
        if (this.playState !== "idle") {
            const reason = "AbortError"
            this.finishedReject && this.finishedReject(reason)
        }
        this.playStateValue = "idle"
    }

    /**
     * @internal
     */
    finish() {
        if (this.playStateSource === "running") {
            this.playStateValue = "finished"
            this.driver.finish()
        }
    }

    /**
     * @internal
     */
    isFinished() {
        return this.playStateValue === "finished"
    }
}
